import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { CloseIcon } from '../components/ui.js';

/**
 * Transient notifications.
 *
 * ## Why a live region and not a modal
 *
 * A toast reports something the caller did not ask about — "the invitation was
 * sent". Blocking the page for that would be hostile. So it is a polite live
 * region: announced when the reader is not in the middle of something, ignored
 * otherwise, and never focus-stealing.
 *
 * ## Why errors are not auto-dismissed quickly
 *
 * A success message can disappear in four seconds; the reader has already got
 * what they wanted. A failure carries information they need to act on, and a
 * message that vanishes while it is being read is worse than no message. Errors
 * get a longer window and an explicit close button, and the pages that produce
 * them also render the failure inline next to the control that caused it.
 *
 * ## Why the ids are monotonic
 *
 * React keys need to be stable across re-renders, and `Date.now()` collides when
 * two toasts are pushed in the same millisecond — which happens, because a page
 * that fires three requests and fails all three pushes three toasts in one tick.
 */

type ToastTone = 'success' | 'danger' | 'info';

interface Toast {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
}

interface ToastContextValue {
  push: (toast: { tone?: ToastTone; title: string; description?: string }) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DISMISS_AFTER_MS: Record<ToastTone, number> = {
  success: 4000,
  info: 6000,
  // Long enough to read a sentence and decide what to do about it.
  danger: 12_000,
};

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback<ToastContextValue['push']>(
    ({ tone = 'info', title, description }) => {
      const id = nextId.current;
      nextId.current += 1;

      setToasts((current) => {
        // Cap the stack. Ten queued toasts cover the page and the oldest are the
        // least relevant, so the newest win.
        const next = [...current, { id, tone, title, ...(description ? { description } : {}) }];
        return next.length > 4 ? next.slice(next.length - 4) : next;
      });

      timers.current.set(
        id,
        window.setTimeout(() => dismiss(id), DISMISS_AFTER_MS[tone]),
      );
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/*
        `aria-live="polite"` on the container, and the container is always present
        in the DOM — an element that is inserted with content already inside it is
        not reliably announced, because the live region has to exist before the
        content arrives.
      */}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:items-end sm:p-5"
      >
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

const TONE_STYLES: Record<ToastTone, string> = {
  success: 'border-success-line bg-success-soft',
  danger: 'border-danger-line bg-danger-soft',
  info: 'border-line-strong bg-surface',
};

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }): ReactNode {
  return (
    <div
      role={toast.tone === 'danger' ? 'alert' : 'status'}
      className={`pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-lg border px-3.5 py-3 shadow-pop ${TONE_STYLES[toast.tone]}`}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[0.8125rem] font-medium text-ink">{toast.title}</p>
        {toast.description ? (
          <p className="mt-0.5 text-xs break-anywhere text-ink-muted">{toast.description}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={`Dismiss: ${toast.title}`}
        className="-mr-1 shrink-0 rounded-md p-1 text-ink-subtle transition-colors hover:bg-surface-2 hover:text-ink"
      >
        <CloseIcon />
      </button>
    </div>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast was called outside <ToastProvider>. Wrap the route tree in App.tsx.');
  }
  return context;
}
